<template>
  <el-dialog
    title="完整终端"
    :visible.sync="dialogVisible"
    width="80%"
    :before-close="handleClose"
    custom-class="full-terminal-dialog"
    :modal="false"
    @opened="onDialogOpened"
    @closed="onDialogClosed">

    <div class="terminal-toolbar">
      <span class="terminal-info">
        <i class="el-icon-folder"></i>
        {{ currentWorkingDir }}
      </span>
      <el-button-group size="mini">
        <el-button type="danger" @click="sendInterrupt">
          <i class="el-icon-close"></i> Ctrl+C
        </el-button>
        <el-button @click="clearTerminal">
          <i class="el-icon-delete"></i> 清屏
        </el-button>
        <el-button @click="copyOutput">
          <i class="el-icon-document-copy"></i> 复制
        </el-button>
      </el-button-group>
    </div>

    <!-- xterm.js 容器 -->
    <div ref="terminalContainer" class="terminal-container"></div>

    <!-- 手动输入命令 -->
    <div class="terminal-input-area" v-if="allowManualInput">
      <el-input
        v-model="manualCommand"
        placeholder="输入命令并按回车..."
        @keyup.enter.native="sendManualCommand">
        <template slot="prepend">$</template>
      </el-input>
    </div>
  </el-dialog>
</template>

<script>
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { A2AClient } from '@/utils/A2AClient.js';

export default {
  name: 'FullTerminalDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    streamId: {
      type: String,
      required: true
    },
    agent: {
      type: Object,
      required: true
    },
    allowManualInput: {
      type: Boolean,
      default: true
    },
    // 🔥 初始输出内容
    initialOutput: {
      type: String,
      default: ''
    },
    // 🔥 当前工作目录
    cwd: {
      type: String,
      default: '~'
    }
  },
  mounted() {
    // 如果 visible 为 true，立即初始化终端
    if (this.visible) {
      this.$nextTick(() => {
        this.onDialogOpened();
      });
    }
  },
  data() {
    return {
      terminal: null,
      fitAddon: null,
      manualCommand: '',
      currentWorkingDir: this.cwd || '~',
      // 🔥 实时输出监听处理器
      outputHandler: null
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    }
  },
  methods: {
    onDialogOpened() {
      // 使用 $nextTick 确保 DOM 已渲染
      this.$nextTick(() => {
        this.initTerminal();
        this.subscribeToOutput();
      });
    },

    initTerminal() {
      if (!this.$refs.terminalContainer) {
        return;
      }

      try {
        // 创建 xterm.js 实例
        this.terminal = new Terminal({
          cursorBlink: true,
          fontSize: 14,
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          theme: {
            background: '#1e1e1e',
            foreground: '#d4d4d4',
            cursor: '#ffffff',
            selection: 'rgba(255, 255, 255, 0.3)',
            black: '#000000',
            red: '#e06c75',
            green: '#98c379',
            yellow: '#d19a66',
            blue: '#61afef',
            magenta: '#c678dd',
            cyan: '#56b6c2',
            white: '#abb2bf',
            brightBlack: '#5c6370',
            brightRed: '#e06c75',
            brightGreen: '#98c379',
            brightYellow: '#d19a66',
            brightBlue: '#61afef',
            brightMagenta: '#c678dd',
            brightCyan: '#56b6c2',
            brightWhite: '#ffffff'
          },
          allowTransparency: true,
          scrollback: 10000
        });

        // 加载 FitAddon
        this.fitAddon = new FitAddon();
        this.terminal.loadAddon(this.fitAddon);

        // 打开终端
        this.terminal.open(this.$refs.terminalContainer);
        this.fitAddon.fit();

        // 使用 setTimeout 确保终端完全初始化后再加载内容
        setTimeout(() => {
          this.loadHistoryOutput();
        }, 100);

        // 监听用户输入
        if (this.allowManualInput) {
          this.terminal.onData((data) => {
            this.sendTerminalInput(data);
          });
        }

        // 监听窗口大小变化
        window.addEventListener('resize', this.handleResize);

      } catch (error) {
        console.error('[FullTerminalDialog] Error initializing terminal:', error);
      }
    },

    loadHistoryOutput() {
      if (!this.terminal) {
        return;
      }

      // 使用 initialOutput prop 显示历史输出
      if (this.initialOutput && this.initialOutput.length > 0) {
        try {
          // 强制转换为字符串并处理换行符
          let content = String(this.initialOutput);
          // 将 Unix 换行符 \n 转换为终端换行符 \r\n
          content = content.replace(/\r?\n/g, '\r\n');
          this.terminal.write(content);
        } catch (error) {
          console.error('[FullTerminalDialog] Error writing initial output:', error);
          this.terminal.write('\r\n[Error loading terminal output]\r\n');
        }
      } else {
        this.terminal.write('\r\n[等待终端输出...]\r\n');
      }
    },

    subscribeToOutput() {
      // 创建输出处理器
      this.outputHandler = (data) => {
        // 只处理当前 streamId 的输出
        if (data.streamId === this.streamId && data.output) {
          // 🔥 Roo-Code 已经按行发送完整数据，直接写入
          if (this.terminal) {
            // 确保换行符格式正确：\n → \r\n
            let content = data.output.replace(/\r?\n/g, '\r\n');
            this.terminal.write(content);
          }
        }
      };

      // 🔥 使用静态全局事件总线订阅
      A2AClient.onGlobalEvent('terminal-output', this.outputHandler);
    },

    unsubscribeFromOutput() {
      if (this.outputHandler) {
        A2AClient.offGlobalEvent('terminal-output', this.outputHandler);
        this.outputHandler = null;
      }
    },

    async sendTerminalInput(data) {
      // xterm 用户直接输入 - 直接发送原始数据
      if (!this.$agentSessionManager) {
        console.error('[FullTerminalDialog] AgentSessionManager 未初始化');
        return;
      }

      try {
        await this.$agentSessionManager.sendTerminalCommand(this.streamId, data, { action: 'input' });
      } catch (error) {
        console.error('[FullTerminalDialog] 发送终端输入失败:', error);
      }
    },

    async sendManualCommand() {
      if (this.manualCommand.trim()) {
        const command = this.manualCommand.trim();

        if (!this.$agentSessionManager) {
          console.error('[FullTerminalDialog] AgentSessionManager 未初始化');
          return;
        }

        try {
          // 发送到远程执行（添加换行符让命令执行）
          await this.$agentSessionManager.sendTerminalCommand(this.streamId, command + '\n', { action: 'input' });
          this.manualCommand = '';
        } catch (error) {
          console.error('[FullTerminalDialog] 发送命令失败:', error);
          this.$message.error('发送命令失败');
        }
      }
    },

    async sendInterrupt() {
      if (!this.$agentSessionManager) {
        console.error('[FullTerminalDialog] AgentSessionManager 未初始化');
        this.$message.error('无法发送中断信号');
        return;
      }

      try {
        // 发送 Ctrl+C 中断信号（ASCII 码 0x03）
        await this.$agentSessionManager.sendTerminalCommand(this.streamId, '\x03', { action: 'input' });

        // 本地显示提示
        this.terminal.write('\r\n^C\r\n');
      } catch (error) {
        console.error('[FullTerminalDialog] 发送Ctrl+C失败:', error);
        this.$message.error('发送中断信号失败');
      }
    },

    clearTerminal() {
      this.terminal.clear();
    },

    copyOutput() {
      this.terminal.selectAll();
      document.execCommand('copy');
      this.terminal.clearSelection();
      this.$message.success('已复制到剪贴板');
    },

    handleResize() {
      if (this.fitAddon) {
        this.fitAddon.fit();
      }
    },

    handleClose() {
      this.$emit('update:visible', false);
    },

    onDialogClosed() {
      this.destroyTerminal();
    },

    destroyTerminal() {
      this.unsubscribeFromOutput();
      window.removeEventListener('resize', this.handleResize);
      if (this.terminal) {
        this.terminal.dispose();
        this.terminal = null;
      }
    }
  },

  beforeDestroy() {
    this.destroyTerminal();
  }
};
</script>

<style lang="scss">
.full-terminal-dialog {
  .el-dialog__body {
    padding: 0;
    background: #1e1e1e;
  }

  .terminal-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #2d2d2d;
    border-bottom: 1px solid #404040;

    .terminal-info {
      color: #d4d4d4;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        color: #61afef;
      }
    }
  }

  .terminal-container {
    height: 500px;
    padding: 8px;
    overflow: hidden;
  }

  .terminal-input-area {
    padding: 12px 16px;
    background: #2d2d2d;
    border-top: 1px solid #404040;

    .el-input-group__prepend {
      background: #1e1e1e;
      color: #98c379;
      border-color: #404040;
      font-family: 'Menlo', monospace;
    }

    .el-input__inner {
      background: #1e1e1e;
      color: #d4d4d4;
      border-color: #404040;
      font-family: 'Menlo', monospace;

      &:focus {
        border-color: #61afef;
      }
    }
  }
}
</style>
